import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneIcon from '@mui/icons-material/Phone';
import { Avatar, Stack, Typography } from '@mui/material';
import { ContactDetails, Qualifications } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  contactDetails: ContactDetails | null;
  isActive: boolean;
  qualification?: string;
  disableShadow?: boolean;

  qualificationToText(): string;
}

const ContactCard: React.FC<Props> = ({ contactDetails, isActive, qualification, disableShadow, qualificationToText }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const getQualificationLetter = () => {
    switch (qualification as Qualifications) {
      case Qualifications.A:
        return 'A.';
      case Qualifications.B:
        return 'B.';
      case Qualifications.C:
        return 'C.';
      default:
        return '';
    }
  };

  return (
    <div className={cx(classes.contactCard, disableShadow ? 'no-shadow' : undefined)}>
      <Stack className={classes.cardHead} direction={'row'} gap={2}>
        <Avatar sx={{ width: 70, height: 70 }} />
        <Stack direction={'row'} justifyContent={'space-between'} sx={{ flex: 1 }}>
          <Stack>
            <Stack direction={'row'} alignItems={'center'} gap={1} mb={'4px'}>
              <Typography className={classes.titleText}>{contactDetails?.fullName}</Typography>
              {isActive ? (
                <>
                  <Typography className={classes.titleText}>|</Typography>
                  <CheckCircleIcon className={classes.successIcon} sx={{ marginBottom: '2px' }} />
                  <Typography className={classes.greyText}>{t('addedToPipeline')}</Typography>
                </>
              ) : null}
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <LinkedInIcon className={classes.icon} />
              {contactDetails?.linkedinUrl ? (
                <PaiLink
                  text={_.truncate(contactDetails?.linkedinUrl, { length: 50 })}
                  underline={'always'}
                  href={contactDetails?.linkedinUrl}
                  target={'_blank'}
                  size={'md'}
                />
              ) : (
                <Typography className={classes.greyText}>{t('noLinkedInUrl')}</Typography>
              )}
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <AlternateEmailIcon className={classes.icon} />
              <Typography className={classes.greyText}>{contactDetails?.email || t('noEmail')}</Typography>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <PhoneIcon className={classes.icon} />
              <Typography className={classes.greyText}>{contactDetails?.phoneNumber || t('noPhone')}</Typography>
            </Stack>
          </Stack>
          <Stack alignItems={'flex-end'}>
            <Typography className={classes.ratingLetter}>{getQualificationLetter()}</Typography>
            <Typography className={classes.ratingPrimaryText}>{qualificationToText()}</Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack className={classes.cardInfo} direction={'row'}>
        <div className={classes.cardInfoContent}>
          <Typography className={classes.boldText}>{contactDetails?.occupation || '-'}</Typography>
          <Typography className={classes.greyText} mt={'auto'}>
            {t('jobTitle')}
          </Typography>
        </div>
        <div className={classes.cardInfoContent}>
          <Typography className={classes.boldText}>{contactDetails?.companyName || '-'}</Typography>
          <Typography className={classes.greyText} mt={'auto'}>
            {t('company')}
          </Typography>
        </div>
        <div className={classes.cardInfoContent}>
          <Typography className={classes.boldText}>{contactDetails?.location || '-'}</Typography>
          <Typography className={classes.greyText} mt={'auto'}>
            {t('location')}
          </Typography>
        </div>
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactCard: {
    boxShadow: Shadows.GREY_SHADOW_MAIN,
    borderRadius: '8px',
    background: '#FFF',
    '&.no-shadow': {
      boxShadow: 'unset',
      border: `1px solid ${theme.palette.greys.light}`,
    },
  },
  reviewedBadge: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  cardHead: {
    padding: '12px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bold',
  },
  boldText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  successIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.backgrounds.success,
    background: theme.palette.text.primary,
    borderRadius: '50%',
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
  },
  ratingLetter: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  ratingPrimaryText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  cardInfo: {
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
  cardInfoContent: {
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.greys.light}`,
    },
  },
}));

export default ContactCard;
